/**
 * 지역 장례식장 페이지(`/parlors/region/...`) 비용 프레임 A/B.
 *
 * 무엇을 가르는가
 * - A(대조군): 현행. 히어로 카드가 "내 근처 장례식장 한번에 알아보기"를 약속한다
 * - B: UI 골격(최상단·검은 카드)은 그대로 두고 **약속만** 비용으로 바꾼다.
 *   "{지역} 장례 비용 알아보기" + 근거 문구 + 로그인 화면도 같은 약속
 *
 * 왜 UI 를 안 건드리는가
 * - 2026-07-15 빠른폼 도입(최상단 이동 + 검은 배경) 이후 제출률이 12.6% → 13.9% 로 올랐다.
 *   그 이득은 유지하고 약속만 바꾼다. 롤백이 아니다.
 *
 * 왜 약속을 비용으로 되돌리는가
 * - 개편 전(~2026-07-06) 히어로는 "장례 비용 알아보기" 였고 그때 제출→리드가 69.2% 였다.
 *   현행은 27.4% 다. 그 사이 제출 물량이 3배가 돼 희석분이 섞여 있지만, 물량이 늘지 않은
 *   오가닉 셀에서도 같은 폭으로 빠져 희석만으로는 설명되지 않는다.
 * - 목록을 약속해 놓고 로그인 벽을 세우면 "목록 보려고 가입까지" 가 된다. 비용은 그 자리에서
 *   줄 수 없는 것이라 가입 요구가 자연스럽다. 맥락을 맞추는 쪽에 건다.
 *
 * 왜 화면 두 개를 같은 키로 묶는가
 * - 카드에서 비용을 약속하고 로그인에서 다른 말을 하면 약속이 끊긴다. Hackle 배정은 사용자
 *   단위로 sticky 해서 같은 키를 쓰면 두 화면이 같은 variant 로 묶인다. #344 와 같은 이유다.
 */

/**
 * 실험 모수를 가르는 지면 종류. 두 실험은 이 값으로 상호 배타가 된다.
 *
 * ## 왜 광고/오가닉이 아니라 시도/시군구로 가르는가
 *
 * 최근 실험들(#342·343, #345·346, #347·348)은 광고/오가닉으로 갈랐다. 그 경로에서는 두 유입의
 * 기저 회원가입률 차이가 커서다(12.6% vs 33.9%).
 *
 * 이 지면은 다르다. 2026-07~08 실측에서 **지면 종류가 더 크게 가른다.**
 * - 제출률: 시군구 10.8~13.7% vs 시도 17.6~18.1% (약 1.5배)
 * - 유효CVR: 시군구 1.5~1.8% vs 시도 2.0~2.4% (약 1.4배)
 * - 같은 지면 안에서 광고와 오가닉의 차이는 이보다 작다(오가닉 10.8 vs 18.1 로 축 내부
 *   분산이 축 간 차이보다 크다)
 *
 * 나누면 arm 당 모수가 반으로 줄어 판정이 2배 느려진다(단일 2.4개월 → 시군구 4개월,
 * 시도 6개월, 유효CVR +30% 기준). 그래도 나누기로 한 것은 영민 판단이다. 효과가 크면
 * 빨리 드러나고, 시도 쪽은 광고 물량을 더 태워 모수를 키울 수 있다(2026-09-15).
 */
export type RegionPageUnit = "sigungu" | "sido";

/**
 * ⚠️ Hackle 콘솔에 실제로 만든 실험의 key 와 반드시 같아야 한다.
 *
 * **매핑을 추측하지 말 것.** #340/#341 은 콘솔 실제 매핑이 340=at-needs, 341=여유 로
 * 긴급도가 낮은 쪽이 뒤 번호였다. 뒤집혀도 화면은 정상 렌더되고 할당만 엉켜서 조용히
 * 실패한다.
 *
 * **350 을 건너뛴 이유**: 그 번호는 장지 실험이 이미 쓰고 있다(2026-09-16 영민 확인).
 * 코드베이스에는 350 이 없어서 grep 으로는 안 잡히고 콘솔에만 있다. 연번이 비어 보여도
 * 다시 쓰지 말 것.
 *
 * 0 이면 어느 화면도 실험 컨테이너를 마운트하지 않고 현행(A)으로 흐른다.
 */
const EXPERIMENT_KEY_BY_UNIT: Record<RegionPageUnit, number> = {
  /** 지역 페이지 비용 프레임 - 시군구 (`/parlors/region/경기-평택시`) */
  sigungu: 349,
  /** 지역 페이지 비용 프레임 - 시도 (`/parlors/region/경기`). 350 은 장지 실험 점유 */
  sido: 351,
};

/**
 * 지면 종류 마킹 키.
 *
 * 로그인 화면(`/login`)은 사용자가 어느 지면에서 왔는지 모른다. `getRegionQuoteContext()` 의
 * 지역은 **폼에서 고른 값**이지 지면 종류가 아니다. 시도 페이지에서 시군구를 고른 사람
 * (`MubinsoQuickCard` 주석 기준 34%)이 시군구로 분류되면 카드와 로그인이 다른 키를 타
 * "카드는 B, 로그인은 A" 조합이 생긴다. 그래서 지면이 자기 종류를 직접 남긴다.
 *
 * 탭 단위(sessionStorage)라 탭을 닫으면 지워지고, 카카오 왕복은 같은 탭이라 값이 유지된다.
 */
const PAGE_UNIT_KEY = "region_cost_frame_unit";

const isRegionPageUnit = (value: unknown): value is RegionPageUnit =>
  value === "sigungu" || value === "sido";

/**
 * 지면 종류를 세션에 남긴다. **effect 에서 호출할 것** (렌더 중 쓰기 금지).
 *
 * 지면 자체의 판정은 이 마킹 없이도 props 로 성립한다. 마킹이 필요한 건 지면을 떠난
 * **다음**, 즉 로그인 화면이다.
 */
export const markRegionPageUnit = (unit: RegionPageUnit) => {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(PAGE_UNIT_KEY, unit);
  } catch {
    // 사파리 프라이빗 등 쓰기 실패. 마킹이 없으면 로그인 화면은 현행(A)으로 흐른다
  }
};

/** 남겨둔 지면 종류. 없으면 null (모수가 아니다) */
export const getRegionPageUnit = (): RegionPageUnit | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(PAGE_UNIT_KEY);
    return isRegionPageUnit(raw) ? raw : null;
  } catch {
    return null;
  }
};

/**
 * 지면별 실험 키.
 *
 * @param unit 지면 종류. null 이면 모수가 아니다
 * @returns 실험 키. 0 이면 마운트하지 말 것 (마운트하는 순간 노출이 기록된다)
 */
export const resolveRegionCostFrameKey = (unit: RegionPageUnit | null): number =>
  unit ? EXPERIMENT_KEY_BY_UNIT[unit] : 0;

/**
 * B·C안 카피.
 *
 * 문자열만 모아 둔다. 두 컨테이너(`ParlorRegionContainer`, `ParlorsRegionCodeSigunguContainer`)가
 * 같은 값을 써야 하는데 그 둘이 이미 near-duplicate 라, 카피까지 양쪽에 복사하면 한쪽만
 * 고치는 사고가 난다.
 *
 * @param regionLabel "경기 평택시" 또는 "경기" 처럼 화면에 그대로 박을 지역명
 */
export const buildRegionCostFrameCopy = (regionLabel: string) => {
  /** 한 줄에 들어가야 하는 자리(CTA)에 쓰는 짧은 지역명. "경기 평택시" -> "평택시" */
  const shortRegionLabel = regionLabel.split(" ").filter(Boolean).pop() ?? regionLabel;

  return {
    badge: "비용 확인",

    /*
     * 제목 두 줄. 1행 "{지역} 장례 비용", 2행 "빠르게 알아보기".
     * 지역과 파는 것(비용)을 한 줄에 붙이고 동사를 아래로 내린다.
     */
    titleRegion: regionLabel,
    /** 주황 강조. 이 카드가 파는 것이 무엇인지가 여기 있다 */
    titleAccent: "장례 비용",
    titleBottom: "빠르게 알아보기",

    /*
     * 설명 두 줄. 1행은 왜 알아봐야 하는지(비용 편차), 2행은 무엇을 받는지다.
     * A 가 "조건에 맞는 이용 가능한 장례식장을 찾아드려요" 로 목록을 약속하는 자리와 갈린다.
     */
    descLine1: "지역과 상황에 따라 비용이 크게 달라요.",
    descLine2Lead: "몇 가지만 선택하여 ",
    /** 볼드 + 흰색. "줄일 수 있는 항목" 이 아래 절감 근거 블록과 이어진다 */
    descLine2Accent: "예상 비용과 줄일 수 있는 항목",
    descLine2Tail: "까지 알아보세요.",

    /**
     * 2026-07-06 이전 히어로에 있던 문구를 그대로 되살린다.
     *
     * ⚠️ 246만원의 산출 근거가 코드에 없다. `HomeRequestBox.tsx` 에 최소 2024년부터 하드코딩된
     * 값이고, "장례비용 평균 통계(약 500만원)와 고이 1년 평균의 차액" 이라는 것이 영민의 추정이다
     * (2026-09-15, 미검증). 근거가 확인되면 이 주석을 사실로 바꾸고, 수치가 틀렸으면 문구에서
     * 숫자를 빼는 편이 낫다. 사용자에게 보여주는 사실 주장이다.
     */
    socialProof: {
      /** `HomeRequestBox` 가 쓰던 절감 아이콘 그대로. 같은 말을 같은 그림으로 한다 */
      icon: "platform/home/saving.png",
      lead: "비용을 미리 알아본 사람들은",
      body: "장례 비용을 ",
      amount: "평균 246만원",
      tail: " 아끼고 있어요",
    },

    /** B안 제출 버튼. 지역명을 짧게 넣는 것은 버튼이 한 줄이어야 하기 때문이다 */
    submitText: `${shortRegionLabel} 장례 비용 확인하기`,

    /**
     * 히어로가 화면에서 사라졌을 때 하단에 뜨는 버튼 문구. **제목과 같은 말**이다.
     *
     * 제출 버튼을 그대로 쓰지 않는 이유: 그 말은 폼에서 조건을 이미 고른 사람에게 성립한다.
     * 스크롤로 폼을 지나친 사람은 아직 아무것도 안 골랐다. 제목을 반복해 "여기서 그걸 받을
     * 수 있다" 만 다시 알린다.
     *
     * 시도/시군구 지면에는 고정 CTA 가 하나도 없다(`CallFixedButton` 은 service 지면 전용).
     * 2026-08 실측에서 90% 스크롤 세션 2,888건 중 2,041건(70.7%)이 제출 없이 끝났고,
     * 312건은 스크롤한 뒤 되돌아와 제출했다. 돌아오는 행동이 이미 있는데 길이 없다.
     */
    fixedCtaText: `${regionLabel} 장례 비용 알아보기`,

    /**
     * 제출 버튼 모서리. 미지정 시 기존 pill(36px) 이라 알약처럼 보인다.
     *
     * 이 카드의 다른 면(카드 20px, 흰 폼 영역 14px)이 전부 사각에 가까워서, 버튼만 알약이면
     * 따로 논다. A 는 현행 그대로 두고 B·C 에서만 맞춘다.
     */
    submitBorderRadius: "10px",

    /**
     * C안 전용. 폼 첫 질문으로 2갈래를 넣고, 장례식장 쪽은 직접 고르게 한다.
     *
     * 폼 **밖**의 오렌지 배너(`RegionCostCard`)가 하던 역할을 폼 안으로 가져온 것이다.
     * 그 배너의 방문 대비 제출은 0.64~1.60% 로, 같은 자리에 두면 결과가 같을 가능성이 높다.
     */
    cost: {
      tabQuestion: "어떤 비용을 확인할까요?",
      tabTotal: "장례 총 비용",
      tabParlor: "장례식장별 비용",

      /** 총비용 탭 안내. 무엇을 안 골라도 되는지를 먼저 말해 부담을 지운다 */
      guideTotalTitle: "희망 지역과 준비 상황만 선택해주세요.",
      guideTotalBody:
        "장례의 가장 큰 비용을 차지하는 장례식장, 장지, 화장장 비용을 확인하시고 불필요한 비용을 줄여보세요.",

      /** 장례식장 탭 안내. "건너뛰어도 괜찮다" 가 선택을 필수로 오해하지 않게 한다 */
      guideParlorTitle: "궁금한 장례식장을 골라보세요.",
      guideParlorBody:
        "정하지 않으셨다면 건너뛰어도 괜찮아요. 장례식장 비용은 조문객 수에 따라 빈소, 음식 비용이 크게 달라져요.",

      pickerQuestion: "어떤 장례식장이 궁금하신가요?",
      /** @param where 폼에서 고른 지역명. 페이지 지역이 아니라 사용자가 고른 값이다 */
      pickerHint: (where: string) =>
        `${where}의 장례식장을 보여드려요. 여러 곳을 골라 비교할 수 있어요.`,
      pickerPlaceholder: "장례식장 선택 (선택 사항)",
      /**
       * 시/군/구를 고르기 전에는 장례식장을 못 고른다.
       *
       * 시도만 고른 상태에서 열면 그 시도 전체(부산 60곳)가 나오고, 고른 장례식장과 폼의
       * 지역이 어긋난 채로 제출된다. 목록도 너무 길어 고르는 행위 자체가 어려워진다.
       */
      pickerLocked: "시/군/구를 먼저 선택해주세요",
      pickerEmpty: "이 지역에는 등록된 장례식장이 없어요",

      sheetTitle: "장례식장 선택",
      sheetHint: "중복 선택 가능",
      sheetDone: "선택 완료",
      /** @param count 고른 장례식장 수 */
      sheetCount: (count: number) => `${count}곳 선택`,

      submitTotal: `${shortRegionLabel} 장례 총 비용 확인하기`,
      submitParlorEmpty: `${shortRegionLabel} 장례식장별 비용 확인하기`,
      submitParlorPicked: "선택한 장례식장 비용 확인하기",
    },
  };
};
